import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { initPostHog, posthog } from "@/lib/posthog";
import { useAuth } from "@/lib/auth-context";

export function PostHogTracker() {
  const { user, profile } = useAuth() as { user: { id: string; email: string } | null; profile: { first_name?: string | null; last_name?: string | null } | null };
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const identifiedRef = useRef<string | null>(null);

  const safePostHog = (callback: () => void) => {
    try {
      callback();
    } catch {
      // Analytics should never break the app shell or auth flow.
    }
  };

  useEffect(() => {
    safePostHog(() => {
      initPostHog();
    });
  }, []);

  // Identify / reset on auth change
  useEffect(() => {
    if (user?.id) {
      if (identifiedRef.current !== user.id) {
        safePostHog(() => {
          posthog.identify(user.id, {
            email: user.email,
            name: [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || undefined,
          });
        });
        identifiedRef.current = user.id;
      }
    } else if (identifiedRef.current) {
      safePostHog(() => {
        posthog.reset();
      });
      identifiedRef.current = null;
    }
  }, [user?.id, user?.email, profile?.first_name, profile?.last_name]);

  // Capture pageview on route change
  useEffect(() => {
    if (!pathname) return;
    safePostHog(() => {
      posthog.capture("$pageview", { $pathname: pathname });
    });
  }, [pathname]);

  return null;
}
